<script setup>
import { ref, watch, nextTick } from 'vue';
import { useToggle } from '@vueuse/core';
import { OnClickOutside } from '@vueuse/components';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  columns: {
    type: Array,
    required: true,
  },
  visibleColumns: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(['update:visibleColumns']);

const [showMenu, toggleMenu] = useToggle(false);
const buttonRef = ref(null);
const menuStyle = ref({});

const menuPadding = 12;

const updateMenuPosition = () => {
  if (buttonRef.value) {
    const rect = buttonRef.value.getBoundingClientRect();
    const menuWidth = 280;

    let left = rect.right - menuWidth;
    if (left < menuPadding) {
      left = rect.left;
    }

    menuStyle.value = {
      position: 'fixed',
      top: `${rect.bottom + 4}px`,
      left: `${left}px`,
      width: `${menuWidth}px`,
      zIndex: 9999,
    };
  }
};

watch(showMenu, async isVisible => {
  if (isVisible) {
    await nextTick();
    updateMenuPosition();
  }
});

const toggleColumn = columnKey => {
  const newVisibleColumns = [...props.visibleColumns];
  const index = newVisibleColumns.indexOf(columnKey);

  if (index > -1) {
    if (newVisibleColumns.length > 1) {
      newVisibleColumns.splice(index, 1);
    }
  } else {
    newVisibleColumns.push(columnKey);
  }

  emit('update:visibleColumns', newVisibleColumns);
};

const isColumnVisible = columnKey => {
  return props.visibleColumns.includes(columnKey);
};

const resetToDefaults = () => {
  const defaultColumns = props.columns.map(col => col.key);
  emit('update:visibleColumns', defaultColumns);
};
</script>

<template>
  <div ref="buttonRef" class="relative">
    <Button
      slate
      outline
      size="sm"
      icon="i-lucide-columns"
      @click="toggleMenu()"
    >
      Colunas
    </Button>

    <teleport to="body">
      <OnClickOutside @trigger="toggleMenu(false)">
        <div
          v-if="showMenu"
          :style="menuStyle"
          class="box-border border rounded-lg bg-n-alpha-3 backdrop-blur-[100px] fixed shadow-lg border-n-strong dark:border-n-strong p-4 z-[9999]"
        >
          <div class="flex items-center justify-between mb-4">
            <h4 class="m-0 text-sm font-medium text-n-slate-12">
              Colunas Visíveis
            </h4>
            <Button
              ghost
              slate
              xs
              icon="i-lucide-x"
              @click="toggleMenu(false)"
            />
          </div>

          <div class="space-y-2 max-h-64 overflow-y-auto mb-4">
            <label
              v-for="column in columns"
              :key="column.key"
              class="flex items-center space-x-2 cursor-pointer hover:bg-n-solid-4 p-2 rounded"
              :class="{
                'opacity-50':
                  isColumnVisible(column.key) && visibleColumns.length === 1,
              }"
            >
              <input
                type="checkbox"
                :checked="isColumnVisible(column.key)"
                :disabled="
                  isColumnVisible(column.key) && visibleColumns.length === 1
                "
                class="w-4 h-4 text-blue-600 border-n-weak rounded focus:ring-blue-500"
                @change="toggleColumn(column.key)"
              />
              <span class="text-sm text-n-slate-12">{{ column.label }}</span>
            </label>
          </div>

          <div class="pt-3 border-t border-n-weak">
            <Button
              variant="ghost"
              color="slate"
              size="sm"
              class="w-full"
              @click="resetToDefaults"
            >
              Restaurar Padrões
            </Button>
          </div>
        </div>
      </OnClickOutside>
    </teleport>
  </div>
</template>
